<template>
  <ConfirmDeleteModal
    :media="true"
    @confirm="deleteThisMediaFile()"
    @cancel="closeConfirmDeleteModal()"
    v-if="isConfirmDeleteModalVisible"
  >
    <template #heading>Delete this image?</template>
    <template #subheading> </template>
  </ConfirmDeleteModal>
  <CategoriesListModal
    v-if="isCategoryListModalVisible"
    @cancel="closeCategoryListModal"
    @setNewCategories="handleNewCategories"
  ></CategoriesListModal>
  <CreateCategoryModal
    v-if="isNewCategoryModalVisible"
    @cancel="closeCreateCategoryModal"
    @setNewCategory="setExistingCategory"
  ></CreateCategoryModal>
  <TagsListModal
    v-if="isTagsListModalVisible"
    @cancel="closeTagsListModal"
    @setNewTag="handleNewTags"
  ></TagsListModal>
  <CreateTagsModal
    v-if="isNewTagModalVisible"
    @cancel="closeCreateTagModal"
    @setNewTag="setExistingTag"
  ></CreateTagsModal>
  <NeighborhoodsListModal
    v-if="isNeighborhoodsListModalVisible"
    @cancel="closeNeighborhoodsListModal"
    @setNewNeighborhood="handleNewNeighborhoods"
  ></NeighborhoodsListModal>
  <CreateNeighborhoodsModal
    v-if="isNewNeighborhoodModalVisible"
    @cancel="closeCreateNeighborhoodModal"
    @setNewNeighborhood="setExistingNeighborhood"
  ></CreateNeighborhoodsModal>
  <VendorOnlineStorePlatformListModal
    v-if="isVendorOnlineStorePlatformListModalVisible"
    @cancel="closeOnlineStorePlatformsListModal"
    @setNewVendorOnlineStorePlatform="handleNewOnlineStorePlatform"
  ></VendorOnlineStorePlatformListModal>
  <CreateVendorOnlineStorePlatformModal
    v-if="isNewVendorOnlineStorePlatformModalVisible"
    @cancel="closeCreateOnlineStorePlatformModal"
    @setNewVendorOnlineStorePlatform="setExistingOnlineStorePlatform"
  ></CreateVendorOnlineStorePlatformModal>
  <AddressListModal
    v-if="isAddressListModalVisible"
    @cancel="showAddressListModal"
    @setNewAddress="setNewAddress"
  ></AddressListModal>
  <CreateAddressModal
    v-if="isCreateAddressModalVisible"
    @cancel="showCreateAddressModal"
    @setNewAddress="setNewAddress"
  ></CreateAddressModal>
  <SuccessfulActionModal v-if="isSuccessful" :vendorEdited="true">
    <template #message>Successfully edited vendor!</template>
    <template #button-1
      ><button class="btn btn-primary me-2" @click.stop="returnToVendorList()">
        Edit another vendor
      </button></template
    >
    <template #button-2>
      <button class="btn btn-secondary" @click.stop="closeSuccessModal()">
        Close
      </button>
    </template>
  </SuccessfulActionModal>

  <div class="bg-danger p-2 mb-2" v-if="updateFailed">
    <p class="mb-0 fw-bold text-white fs-4">
      Something went wrong while attempting to update your vendor. Please try
      again.
    </p>
    <p class="mb-0 fw-bold text-white fs-4">
      If the issue continues, contact your dev team.
    </p>
  </div>
  <template v-if="isLoading"
    ><div class="p-10">
      <LoadingGiftIcon></LoadingGiftIcon></div
  ></template>

  <template v-else-if="!isLoading && selectedVendor">
    <div
      class="d-flex align-items-end justify-content-between text-start my-3 my-lg-5"
    >
      <h1 class="mb-0">Edit Vendor</h1>
      <div class="d-flex align-items-center justify-content-center gap-2">
        <p class="text-danger mb-0" v-if="!checkIfFormIsComplete">
          General Information section is incomplete.
        </p>
        <template v-if="previousVendorId">
          <a class="page-item">
            <router-link
              class="page-link"
              :to="{
                name: 'EditVendor',
                params: { vendorId: previousVendorId },
              }"
              ><b-icon-chevron-left></b-icon-chevron-left></router-link></a
        ></template>
        <div class="dropdown">
          <button
            class="form-select text-start text-truncate"
            style="max-width: 15rem; min-width: 15rem"
            type="button"
            data-bs-toggle="dropdown"
          >
            {{ selectedVendor.name }}
          </button>
          <ul class="dropdown-menu">
            <router-link
              class="dropdown-item"
              v-for="item in vendorList"
              :key="item._id"
              :to="{
                name: 'EditVendor',
                params: { vendorId: item._id },
              }"
              ><template v-if="item._id === vendorId"
                ><span class="text-primary fw-bold">{{
                  item.name
                }}</span></template
              >
              <template v-else> {{ item.name }}</template></router-link
            >
          </ul>
        </div>
        <template v-if="nextVendorId">
          <a class="page-item">
            <router-link
              class="page-link"
              :to="{
                name: 'EditVendor',
                params: { vendorId: nextVendorId },
              }"
              ><b-icon-chevron-right></b-icon-chevron-right></router-link></a
        ></template>
        <div class="d-flex justify-content-end">
          <template v-if="!vendorIsSaving">
            <button
              :class="{ disabled: !checkIfFormIsComplete }"
              class="btn btn-success"
              @click.prevent="updateThisVendor()"
            >
              Save changes
            </button>
          </template>
          <template v-if="vendorIsSaving">
            <button class="btn btn btn-primary" type="button" disabled>
              <span
                class="spinner-grow spinner-grow-sm"
                role="status"
                aria-hidden="true"
              ></span>
              Saving changes...
            </button></template
          >
        </div>
      </div>
    </div>
    <!-- general -->
    <div class="card mb-3 mb-lg-5">
      <div class="card-header bg-dark">
        <h4 class="card-header-title text-white">General information</h4>
      </div>
      <div
        class="d-flex justify-content-center align-items-center text-center bg-soft-danger p-3"
        v-if="!checkIfFormIsComplete"
      >
        <p class="text-danger mb-0 fw-bold">
          Please complete
          <span class="badge bg-soft-info rounded-pill">
            <span class="text-danger fs-6">required</span>
          </span>
          fields.
        </p>
      </div>
      <div class="card-body d-flex flex-column gap-3">
        <!-- name -->
        <div class="row">
          <div class="col-8 text-start">
            <label for="vendorName" class="form-label fw-bold"
              >Vendor Name</label
            >
            <span
              v-if="!getVendorName"
              class="badge bg-soft-info rounded-pill ms-2"
            >
              <span class="text-danger">required</span>
            </span>
            <input
              type="text"
              class="form-control"
              name="vendorName"
              id="vendorName"
              placeholder="Bold Xchange"
              aria-label="Bold Xchange"
              v-model="selectedVendor.name"
            />
          </div>
        </div>
        <!-- Short Description -->
        <div class="row">
          <div class="col text-start">
            <label for="productShortDescription" class="form-label fw-bold"
              >Short description</label
            >
            <textarea
              type="text"
              class="form-control"
              name="vendorDescription"
              id="vendorDescription"
              rows="1"
              placeholder="Strikingly colored premium glassware"
              aria-label="Strikingly colored premium glassware"
              v-model="selectedVendor.metadata.shortDescription"
            ></textarea>
          </div>
        </div>
        <!-- Long Bio -->
        <div class="row">
          <div class="col text-start">
            <label for="productShortDescription" class="form-label fw-bold"
              >Bio</label
            >
            <span
              v-if="!vendorDescription"
              class="badge bg-soft-info rounded-pill ms-2"
            >
              <span class="text-danger">required</span>
            </span>
            <textarea
              type="text"
              class="form-control"
              name="vendorDescription"
              id="vendorDescription"
              rows="5"
              placeholder="Brand partner bio"
              aria-label="Brand partner bio"
              v-model="selectedVendor.description"
            ></textarea>
          </div>
        </div>
        <!-- gift card minimum & typeform url -->
        <div class="row">
          <div class="col-8 text-start">
            <label for="typeformId" class="form-label fw-bold"
              >Typeform URL</label
            >
            <span
              v-if="!getTypeformUrl"
              class="badge bg-soft-info rounded-pill ms-2"
            >
              <span class="text-danger">required</span>
            </span>
            <input
              type="text"
              class="form-control"
              name="typeformUrl"
              id="typeformUrl"
              placeholder="Unique Typeform URL"
              aria-label="Unique Typeform URL"
              v-model="selectedVendor.metadata.typeform_url"
            />
          </div>
          <div class="col-4 text-start">
            <label for="gift_card_minimum" class="form-label fw-bold"
              >Gift card minimum
              <span
                v-if="!isGiftCardMinimumValid || !isGiftCardValueTypeValid"
                class="badge bg-soft-secondary text-info ms-2"
              >
                <span>Must be number</span>
              </span></label
            >
            <input
              type="number"
              class="form-control"
              name="gift_card_minimum"
              id="gift_card_minimum"
              placeholder="10"
              aria-label="10"
              v-model="selectedVendor.metadata.gift_card_minimum"
            />
          </div>
        </div>
        <!-- categories + platforms -->
        <div class="row gy-3">
          <!-- Categories -->
          <div class="col-12 text-start">
            <label for="categories" class="form-label fw-bold"
              >Categories</label
            >
            <span v-if="doesCategoryExist" class="text-danger medium ms-2"
              >Category already exists</span
            >
            <span
              v-if="!vendorCategories || vendorCategories.length === 0"
              class="badge bg-soft-info rounded-pill ms-2"
            >
              <span class="text-danger">required</span>
            </span>
            <div class="input-group">
              <button
                class="form-select d-flex"
                @click.prevent="showCategoryListModal()"
              >
                <span>Select Category(s)</span>
              </button>
              <button
                type="button"
                class="btn btn-warning fw-bold"
                @click.prevent="showCreateCategoryModal()"
              >
                create
              </button>
            </div>

            <!-- category pills -->
            <div class="d-flex flex-row flex-wrap justify-content-left mt-1">
              <template v-if="vendorCategories && vendorCategories.length > 0">
                <div
                  v-for="(category, key) in vendorCategories"
                  :key="'item-' + key"
                >
                  <!-- badge -->
                  <span
                    role="button"
                    class="badge bg-soft-dark rounded-pill me-1"
                    @click.stop="removeCategorySelected(category)"
                  >
                    <b-icon-x-circle class="text-danger"></b-icon-x-circle>
                    <span class="text-body ms-1">{{ category }}</span>
                  </span>
                </div>
              </template>
            </div>
          </div>
          <!-- tags -->
          <div class="col-12 text-start">
            <label for="tags" class="form-label fw-bold">Tags</label>
            <span v-if="doesTagExist" class="text-danger medium ms-2"
              >Tag already exists</span
            >
            <div class="input-group">
              <button
                class="form-select d-flex"
                @click.prevent="showTagsListModal()"
              >
                <span>Select tag(s)</span>
              </button>
              <button
                type="button"
                class="btn btn-warning fw-bold"
                @click.prevent="showCreateTagsModal()"
              >
                create
              </button>
            </div>

            <!-- pills -->
            <div class="d-flex flex-row flex-wrap justify-content-left mt-1">
              <template v-if="vendorTags && vendorTags.length > 0">
                <div v-for="(tag, key) in vendorTags" :key="'item-' + key">
                  <!-- badge -->
                  <span
                    role="button"
                    class="badge bg-soft-dark rounded-pill me-1"
                    @click.stop="removeTagsSelected(tag)"
                  >
                    <b-icon-x-circle class="text-danger"></b-icon-x-circle>
                    <span class="text-body ms-1">{{ tag }}</span>
                  </span>
                </div>
              </template>
            </div>
          </div>
          <!-- gift card platform -->
          <div class="col-12 text-start">
            <label for="onlinePlatforms" class="form-label fw-bold"
              >Gift card platform (Square, Toast, etc.)</label
            >
            <span
              v-if="doesOnlineStorePlatformExist"
              class="text-danger medium ms-2"
              >Gift card platform already exists</span
            >
            <div class="input-group">
              <button
                class="form-select d-flex"
                @click.prevent="showVendorOnlineStoreListModal()"
              >
                <span>Select gift card platform(s)</span>
              </button>
              <button
                type="button"
                class="btn btn-warning fw-bold"
                @click.prevent="showCreateOnlineStorePlatformModal()"
              >
                create
              </button>
            </div>

            <!-- pills -->
            <div class="d-flex flex-row flex-wrap justify-content-left mt-1">
              <template
                v-if="
                  vendorOnlineStorePlatform &&
                  vendorOnlineStorePlatform.length > 0
                "
              >
                <div
                  v-for="(platform, key) in vendorOnlineStorePlatform"
                  :key="'item-' + key"
                >
                  <!-- badge -->
                  <span
                    role="button"
                    class="badge bg-soft-dark rounded-pill me-1"
                    @click.stop="removeOnlineStorePlatformSelected(platform)"
                  >
                    <b-icon-x-circle class="text-danger"></b-icon-x-circle>
                    <span class="text-body ms-1">{{ platform }}</span>
                  </span>
                </div>
              </template>
            </div>
          </div>
          <!-- neighborhoods -->
          <div class="col-12 text-start">
            <label for="onlinePlatforms" class="form-label fw-bold"
              >Neighborhoods</label
            >
            <span v-if="doesNeighborhoodExist" class="text-danger medium ms-2"
              >Neighborhood already exists</span
            >
            <div class="input-group">
              <button
                class="form-select d-flex"
                @click.prevent="showNeighborhoodsListModal()"
              >
                <span>Select neighborhood(s)</span>
              </button>
              <button
                type="button"
                class="btn btn-warning fw-bold"
                @click.prevent="showCreateNeighborhoodModal()"
              >
                create
              </button>
            </div>

            <!-- pills -->
            <div class="d-flex flex-row flex-wrap justify-content-left mt-1">
              <template
                v-if="vendorNeighborhoods && vendorNeighborhoods.length > 0"
              >
                <div
                  v-for="(neighborhood, key) in vendorNeighborhoods"
                  :key="'item-' + key"
                >
                  <!-- badge -->
                  <span
                    role="button"
                    class="badge bg-soft-dark rounded-pill me-1"
                    @click.stop="removeNeighborhoodSelected(neighborhood)"
                  >
                    <b-icon-x-circle class="text-danger"></b-icon-x-circle>
                    <span class="text-body ms-1">{{ neighborhood }}</span>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- address -->
    <div class="card mb-3 mb-lg-5">
      <div class="card-header bg-dark">
        <h4 class="card-header-title text-white">Address (optional)</h4>
      </div>
      <div class="card-body d-flex flex-column gap-3">
        <div class="row">
          <div class="col col-md-8 text-start">
            <!-- Categories -->
            <div class="mb-2">
              <label for="clientName" class="form-label fw-bold"
                >Select/create address</label
              ><span></span>
              <div class="input-group">
                <button
                  class="form-select d-flex"
                  @click.prevent="showAddressListModal()"
                >
                  <span>Select address</span>
                </button>
                <button
                  type="button"
                  class="btn btn-warning fw-bold"
                  @click.prevent="showCreateAddressModal()"
                >
                  create
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body d-flex flex-column gap-3" v-if="vendorAddressId">
        <div class="row">
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="addressLine1"
              >Address line 1</label
            >
            <input
              type="text"
              class="form-control form-control-lg"
              :disabled="!addressIsBeingEdited"
              name="addressLine1"
              id="addressLine1"
              required
              placeholder="Street address, P.O box"
              aria-label="Street address, P.O box"
              v-model="selectedVendor.address.lines[0]"
            />
          </div>
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="addressLine2"
              >Address line 2</label
            >
            <input
              type="url"
              class="form-control form-control-lg"
              :disabled="!addressIsBeingEdited"
              name="addressLine2"
              id="addressLine2"
              required
              placeholder="Apt, suite, unit, etc."
              aria-label="Apt, suite, unit, etc."
              v-model="selectedVendor.address.lines[1]"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-lg-3 text-start">
            <label class="form-label fw-bold" for="city">City</label>
            <span
              v-if="!selectedVendor.address.city"
              class="badge bg-soft-info rounded-pill ms-2"
              ><span class="text-danger">required</span>
            </span>
            <input
              type="url"
              class="form-control form-control-lg"
              :disabled="!addressIsBeingEdited"
              name="city"
              id="city"
              required
              placeholder="St. Louis"
              aria-label="St. Louis"
              v-model="selectedVendor.address.city"
            />
          </div>
          <div class="col-12 col-lg-3 text-start">
            <label class="form-label fw-bold" for="state">State</label>
            <span
              v-if="!selectedVendor.address.state"
              class="badge bg-soft-info rounded-pill ms-2"
              ><span class="text-danger">required</span>
            </span>
            <input
              type="url"
              class="form-control form-control-lg"
              :disabled="!addressIsBeingEdited"
              name="state"
              id="state"
              required
              placeholder="Missouri"
              aria-label="Missouri"
              v-model="selectedVendor.address.state"
            />
          </div>
          <div class="col-12 col-lg-3 text-start">
            <label class="form-label fw-bold" for="postalCode"
              >Postal Code</label
            >
            <input
              type="url"
              class="form-control form-control-lg"
              :disabled="!addressIsBeingEdited"
              name="postalCode"
              id="postalCode"
              required
              placeholder="63139"
              aria-label="63139"
              v-model="selectedVendor.address.postalCode"
            />
          </div>
          <div class="col-12 col-lg-3 text-start">
            <div class="tom-select-custom">
              <label class="form-label fw-bold" for="countrySelect"
                >Country</label
              >
              <select
                class="form-select text-dark"
                :disabled="!addressIsBeingEdited"
                required
                v-model="selectedVendor.address.country"
                name="countrySelect"
                id="countrySelect"
              >
                <option
                  v-for="option in countryOptions"
                  :value="option.value"
                  :key="option.index"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <template v-if="!this.addressIsBeingEdited">
          <div class="d-flex justify-content-end gap-2">
            <button
              class="btn btn-primary fw-bold"
              @click.prevent="editAddress()"
            >
              edit
            </button>
          </div>
        </template>
        <template v-if="this.addressIsBeingEdited">
          <div class="d-flex justify-content-end gap-2">
            <button
              class="btn btn-lg btn-soft-dark"
              @click.prevent="editAddress()"
            >
              cancel
            </button>
            <button
              class="btn btn-lg btn-primary"
              type="button"
              disabled
              v-if="addressIsSaving"
            >
              <span
                class="spinner-grow spinner-grow-sm"
                role="status"
                aria-hidden="true"
              ></span>
              Saving changes...
            </button>
            <button
              type="button"
              class="btn btn-lg btn-primary"
              @click.prevent="updateAddress()"
            >
              Save changes
            </button>
          </div>
        </template>

        <!-- postal code/country -->
      </div>
    </div>
    <!-- contact -->
    <div class="card mb-3 mb-lg-5">
      <div class="card-header bg-dark">
        <h4 class="card-header-title text-white">Contact (optional)</h4>
      </div>
      <div class="card-body d-flex flex-column gap-3">
        <div class="row">
          <div class="col-lg-6 text-start">
            <div class="mb-4">
              <label for="contactName" class="form-label fw-bold">Name</label>
              <input
                type="text"
                class="form-control"
                name="contactName"
                id="contactName"
                placeholder="Joe Kay"
                aria-label="Joe Kay"
                v-model="selectedVendor.contact.name"
              />
            </div>
          </div>
          <div class="col-lg-6 text-start">
            <div class="mb-4">
              <label for="contactTitle" class="form-label fw-bold">Title</label>
              <input
                type="text"
                class="form-control"
                name="contactTitle"
                id="contactTitle"
                placeholder="Chief of Awesomeness"
                aria-label="Chief of Awesomeness"
                v-model="selectedVendor.contact.title"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="card-body d-flex flex-column gap-3">
        <div class="row">
          <div class="col-lg-6 text-start">
            <div class="mb-4">
              <label for="contactEmail" class="form-label fw-bold">Email</label>
              <input
                type="text"
                class="form-control"
                name="contactEmail"
                id="contactEmail"
                placeholder="contactemail@company.com"
                aria-label="contactemail@company.com"
                v-model="selectedVendor.contact.email"
              />
            </div>
          </div>
          <div class="col-lg-6 text-start">
            <div class="mb-4">
              <label for="contactPhone" class="form-label fw-bold">Phone</label>
              <input
                type="text"
                class="form-control"
                name="contactPhone"
                id="contactPhone"
                placeholder="111-111-1111"
                aria-label="111-111-1111"
                v-model="selectedVendor.contact.phone"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- social media -->
    <div class="card mb-3 mb-lg-5">
      <div class="card-header bg-dark">
        <h4 class="card-header-title text-white">Social Media (optional)</h4>
      </div>

      <div class="card-body d-flex flex-column gap-3">
        <div class="row">
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="companyWebsite"
              >Website
            </label>
            <div class="d-flex align-items-center gap-1">
              <input
                type="url"
                class="form-control form-control-lg"
                name="companyWebsite"
                id="companyWebsite"
                title="Go to the company website and copy the URL from your browser's address bar."
                placeholder="Website URL"
                aria-label="Website URL"
                v-model="selectedVendor.metadata.website_url"
              />
            </div>
          </div>
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="gift_card_page"
              >Gift Card Page
            </label>
            <div class="d-flex align-items-center gap-1">
              <input
                type="url"
                class="form-control form-control-lg"
                name="gift_card_page"
                id="gift_card_page"
                title="URL of company gift card purchase"
                placeholder="URL of vendor gift card purchase page"
                aria-label="URL of vendor gift card purchase page"
                v-model="selectedVendor.metadata.gift_card_page"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="twitterProfile"
              >Twitter
            </label>
            <div class="d-flex align-items-center gap-1">
              <input
                type="url"
                class="form-control form-control-lg"
                name="twitterProfile"
                id="twitterProfile"
                title="Go to the company Twitter profile and copy the URL from your browser's address bar."
                placeholder="Twitter URL"
                aria-label="Twitter URL"
                v-model="selectedVendor.metadata.twitter_url"
              />
            </div>
          </div>
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="instagramProfile"
              >Instagram
            </label>
            <div class="d-flex align-items-center gap-1">
              <input
                type="url"
                class="form-control form-control-lg"
                name="instagramProfile"
                id="instagramProfile"
                title="Go to the company Instagram profile and copy the URL from your browser's address bar."
                placeholder="Instagram URL"
                aria-label="Instagram URL"
                v-model="selectedVendor.metadata.instagram_url"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="linkedinProfile"
              >LinkedIn
            </label>
            <div class="d-flex align-items-center gap-1">
              <input
                type="url"
                class="form-control form-control-lg"
                name="linkedinProfile"
                id="linkedinProfile"
                title="Go to the company LinkedIn profile and copy the URL from your browser's address bar."
                placeholder="LinkedIn URL"
                aria-label="LinkedIn URL"
                v-model="selectedVendor.metadata.linkedin_url"
              />
            </div>
          </div>
          <div class="col-lg-6 text-start">
            <label class="form-label fw-bold" for="tiktokProfile"
              >TikTok
            </label>
            <div class="d-flex align-items-center gap-1">
              <input
                type="url"
                class="form-control form-control-lg"
                name="tiktokProfile"
                id="tiktokProfile"
                title="Go to the company TikTok profile and copy the URL from your browser's address bar."
                placeholder="TikTok URL"
                aria-label="TikTok URL"
                v-model="selectedVendor.metadata.tiktok_url"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <!-- logo media -->
      <div class="col-12 mb-3 mb-lg-0 text-start">
        <div class="card mb-3 mb-lg-5">
          <div class="card-header bg-dark">
            <h4 class="card-header-title text-white">Logo media</h4>
          </div>
          <div
            class="d-flex justify-content-center align-items-center text-center bg-soft-info p-3"
          >
            <p class="text-dark mb-0">
              Logo media is utilized as the primary visual representation
              highlighting vendor logos across different collections.
            </p>
          </div>
          <div class="card-body">
            <template
              v-if="!getNewVendorLogosMedia && !getExistingVendorMediaLogos"
            >
              <div class="text-center">
                <p class="text-danger mb-0">No logo media has been uploaded.</p>
                <div class="d-flex flex-column align-items-center">
                  <div class="card-body col-4">
                    <img
                      class="img-fluid card-img"
                      src="../../assets/svg//illustrations/oc-browse.svg"
                      alt="vendor image"
                    />
                  </div>
                  <div class="card-body">
                    <label class="btn btn-outline-primary fs-3" for="logoFiles"
                      >Upload media
                      <input
                        class="form-control"
                        type="file"
                        id="logoFiles"
                        ref="logoFiles"
                        accept="image/*"
                        multiple
                        hidden
                        @change="handleLogoMediaUploads($event)"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </template>
            <!-- existing logo media -->
            <template v-if="getExistingVendorMediaLogos">
              <div class="row g-2">
                <div
                  class="col-6 col-md-3 d-flex flex-column text-center"
                  v-for="image in existingVendorLogos"
                  :key="image._id"
                >
                  <img
                    class="img-fluid card-img img-thumbnail"
                    :src="image.url"
                    alt="logo"
                  />
                  <button
                    :disabled="getNewVendorLogosMedia"
                    class="btn btn-link link-danger fw-bold"
                    @click.prevent="showConfirmDeleteModal(image._id)"
                  >
                    <b-icon-trash></b-icon-trash>
                  </button>
                </div>
              </div>
            </template>
            <!-- new logo media -->
            <template v-if="getNewVendorLogosMedia">
              <div class="row g-2">
                <div
                  class="col-6 col-md-3 d-flex flex-column text-center"
                  v-for="(file, key) in newVendorLogoFiles"
                  :key="'file-' + file.id"
                >
                  <img
                    class="img-fluid card-img img-thumbnail"
                    :id="'image-' + file.id"
                    alt="Image Description"
                  />
                  <span
                    class="btn btn-link link-danger fw-bold"
                    @click.prevent="removeUploadedLogoMediaFile(key)"
                    >Remove</span
                  >
                </div>
              </div>
            </template>
            <template
              v-if="getExistingVendorMediaLogos || getNewVendorLogosMedia"
            >
              <div class="d-flex justify-content-end mt-5">
                <div class="d-flex gap-3">
                  <label
                    class="d-flex align-items-center btn btn-outline-primary mb-0"
                    for="logoFiles"
                    >Add another logo image

                    <input
                      class="form-control"
                      type="file"
                      id="logoFiles"
                      ref="logoFiles"
                      accept="image/*"
                      multiple
                      hidden
                      @change="handleLogoMediaUploads($event)"
                    />
                  </label>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <!-- gift card media -->
      <div class="col-12 mb-3 mb-lg-0 text-start">
        <div class="card mb-3 mb-lg-5">
          <div class="card-header bg-dark">
            <h4 class="card-header-title text-white">Gift card media</h4>
          </div>
          <div
            class="d-flex justify-content-center align-items-center text-center bg-soft-info p-3"
          >
            <p class="text-dark mb-0">
              Gift card media serves as the primary cover image of vendor gift
              cards within various gift card collections.
            </p>
          </div>
          <div class="card-body">
            <template
              v-if="!getExistingGiftCardsMedia && !getNewGiftCardsMedia"
            >
              <div class="text-center">
                <p class="text-danger mb-0">
                  No gift card media has been uploaded..
                </p>

                <div class="d-flex flex-column align-items-center">
                  <div class="card-body col-4">
                    <img
                      class="img-fluid card-img"
                      src="../../assets/svg//illustrations/oc-browse.svg"
                      alt="vendor image"
                    />
                  </div>
                  <div class="card-body">
                    <label
                      class="btn btn-outline-primary fs-3"
                      for="giftCardFiles"
                      >Upload media

                      <input
                        class="form-control"
                        type="file"
                        id="giftCardFiles"
                        ref="giftCardFiles"
                        accept="image/*"
                        multiple
                        hidden
                        @change="handleGiftCardsMediaUploads($event)"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </template>
            <!-- existing gift card media -->
            <template v-if="getExistingGiftCardsMedia">
              <!-- <h2 class="">Gift card media</h2> -->
              <div class="row g-2">
                <div
                  class="col-12 col-lg-3 d-flex flex-column text-center"
                  v-for="image in existingGiftCardsMedia"
                  :key="image._id"
                >
                  <img
                    class="img-fluid card-img"
                    :src="image.url"
                    alt="vendor image"
                  />
                  <button
                    :disabled="getNewGiftCardsMedia"
                    class="btn btn-link link-danger fw-bold"
                    @click.prevent="showConfirmDeleteModal(image._id)"
                  >
                    <b-icon-trash></b-icon-trash>
                  </button>
                </div>
              </div>
            </template>
            <!-- new gift card media -->
            <template v-if="getNewGiftCardsMedia">
              <div class="row g-2">
                <div
                  class="col-12 col-lg-3 d-flex flex-column text-center"
                  v-for="(file, key) in newGiftCardsFiles"
                  :key="'file-' + file.id"
                >
                  <img
                    class="img-fluid card-img"
                    :id="'image-' + file.id"
                    alt="Image Description"
                  />
                  <span
                    class="btn btn-link link-danger fw-bold"
                    @click.prevent="removeUploadedGiftCardsMediaFile(key)"
                    >Remove</span
                  >
                </div>
              </div>
            </template>
            <template v-if="getExistingGiftCardsMedia || getNewGiftCardsMedia">
              <div class="d-flex justify-content-end mt-5">
                <div class="d-flex gap-3">
                  <label
                    class="d-flex align-items-center btn btn-outline-primary mb-0"
                    for="giftCardFiles"
                    >Add another gift card image

                    <input
                      class="form-control"
                      type="file"
                      id="giftCardFiles"
                      ref="giftCardFiles"
                      accept="image/*"
                      multiple
                      hidden
                      @change="handleGiftCardsMediaUploads($event)"
                    />
                  </label>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <!-- branding media -->
      <div class="col-12 mb-3 mb-lg-0 text-start">
        <div class="card mb-3 mb-lg-5">
          <div class="card-header bg-dark">
            <h4 class="card-header-title text-white">Branding media</h4>
          </div>
          <div
            class="d-flex justify-content-center align-items-center text-center bg-soft-info p-3"
          >
            <p class="text-dark mb-0">
              Branding media are images that feature various brand identity
              elements such as products, venues, and more.
            </p>
          </div>
          <div class="card-body">
            <template v-if="!getExistingBrandingMedia && !getNewBrandingMedia">
              <div class="text-center">
                <p class="text-danger mb-0">
                  No branding media has been uploaded.
                </p>

                <div class="d-flex flex-column align-items-center">
                  <div class="card-body col-4">
                    <img
                      class="img-fluid card-img"
                      src="../../assets/svg//illustrations/oc-browse.svg"
                      alt="vendor image"
                    />
                  </div>
                  <div class="card-body">
                    <label
                      class="btn btn-outline-primary fs-3"
                      for="brandingFiles"
                      >Upload media

                      <input
                        class="form-control"
                        type="file"
                        id="brandingFiles"
                        ref="brandingFiles"
                        accept="image/*"
                        multiple
                        hidden
                        @change="handleBrandingMediaUploads($event)"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </template>
            <!-- existing branding media -->
            <template v-if="getExistingBrandingMedia">
              <!-- <h2 class="">Gift card media</h2> -->
              <div class="row g-2">
                <div
                  class="col-6 col-md-3 d-flex flex-column text-center"
                  v-for="image in getExistingBrandingMedia"
                  :key="image._id"
                >
                  <img
                    class="img-fluid card-img"
                    :src="image.url"
                    alt="branding image"
                  />
                  <button
                    :disabled="getNewBrandingMedia"
                    class="btn btn-link link-danger fw-bold"
                    @click.prevent="showConfirmDeleteModal(image._id)"
                  >
                    <b-icon-trash></b-icon-trash>
                  </button>
                </div>
              </div>
            </template>
            <!-- new branding media -->
            <template v-if="getNewBrandingMedia">
              <div class="row g-2">
                <div
                  class="col-6 col-md-3 d-flex flex-column text-center"
                  v-for="(file, key) in newBrandingFiles"
                  :key="'file-' + file.id"
                >
                  <img
                    class="img-fluid card-img"
                    :id="'image-' + file.id"
                    alt="Image Description"
                  />
                  <span
                    class="btn btn-link link-danger fw-bold"
                    @click.prevent="removeUploadedBrandingMediaFile(key)"
                  >
                    Remove
                  </span>
                </div>
              </div>
            </template>
            <template v-if="getExistingBrandingMedia || getNewBrandingMedia">
              <div class="d-flex justify-content-end mt-5">
                <div class="d-flex gap-3">
                  <label
                    class="d-flex align-items-center btn btn-outline-primary mb-0"
                    for="brandingFiles"
                    >Add another branding image

                    <input
                      class="form-control"
                      type="file"
                      id="brandingFiles"
                      ref="brandingFiles"
                      accept="image/*"
                      multiple
                      hidden
                      @change="handleBrandingMediaUploads($event)"
                    />
                  </label>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex align-items-end justify-content-end text-start">
      <div class="d-flex align-items-center justify-content-center gap-2">
        <p class="text-danger mb-0" v-if="!checkIfFormIsComplete">
          General Information section is incomplete.
        </p>
        <template v-if="previousVendorId">
          <a class="page-item">
            <router-link
              class="page-link"
              :to="{
                name: 'EditVendor',
                params: { vendorId: previousVendorId },
              }"
              ><b-icon-chevron-left></b-icon-chevron-left></router-link></a
        ></template>
        <div class="dropdown">
          <button
            class="form-select text-start text-truncate"
            style="max-width: 15rem; min-width: 15rem"
            type="button"
            data-bs-toggle="dropdown"
          >
            {{ selectedVendor.name }}
          </button>
          <ul class="dropdown-menu">
            <router-link
              class="dropdown-item"
              v-for="item in vendorList"
              :key="item._id"
              :to="{
                name: 'EditVendor',
                params: { vendorId: item._id },
              }"
              ><template v-if="item._id === vendorId"
                ><span class="text-primary fw-bold">{{
                  item.name
                }}</span></template
              >
              <template v-else> {{ item.name }}</template></router-link
            >
          </ul>
        </div>
        <template v-if="nextVendorId">
          <a class="page-item">
            <router-link
              class="page-link"
              :to="{
                name: 'EditVendor',
                params: { vendorId: nextVendorId },
              }"
              ><b-icon-chevron-right></b-icon-chevron-right></router-link></a
        ></template>
        <div class="d-flex justify-content-end">
          <template v-if="!vendorIsSaving">
            <button
              :class="{ disabled: !checkIfFormIsComplete }"
              class="btn btn-success"
              @click.prevent="updateThisVendor()"
            >
              Save changes
            </button>
          </template>
          <template v-if="vendorIsSaving">
            <button class="btn btn btn-primary" type="button" disabled>
              <span
                class="spinner-grow spinner-grow-sm"
                role="status"
                aria-hidden="true"
              ></span>
              Saving changes...
            </button></template
          >
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import ConfirmDeleteModal from "../../components/ui/modals/ConfirmDeleteModal.vue";
import SuccessfulActionModal from "../../components/ui/modals/success/SuccessfulActionModal.vue";
import CategoriesListModal from "../../components/ui/modals/lists/CategoriesListModal.vue";
import CreateCategoryModal from "../../components/ui/modals/create/CreateCategoryModal.vue";
import CreateAddressModal from "../../components/ui/modals/create/CreateAddressModal.vue";
import AddressListModal from "../../components/ui/modals/lists/AddressListModal.vue";
import LoadingGiftIcon from "../../components/ui/LoadingGiftIcon.vue";
import VendorOnlineStorePlatformListModal from "../../components/ui/modals/lists/VendorOnlineStorePlatformListModal.vue";
import CreateVendorOnlineStorePlatformModal from "../../components/ui/modals/create/CreateVendorOnlineStorePlatformModal.vue";
import TagsListModal from "../../components/ui/modals/lists/TagsListModal.vue";
import CreateTagsModal from "../../components/ui/modals/create/CreateTagsModal.vue";
import NeighborhoodsListModal from "../../components/ui/modals/lists/NeighborhoodsListModal.vue";
import CreateNeighborhoodsModal from "../../components/ui/modals/create/CreateNeighborhoodsModal.vue";

export default {
  name: "EditVendorForm",
  components: {
    ConfirmDeleteModal,
    SuccessfulActionModal,
    CategoriesListModal,
    CreateCategoryModal,
    CreateAddressModal,
    AddressListModal,
    LoadingGiftIcon,
    VendorOnlineStorePlatformListModal,
    CreateVendorOnlineStorePlatformModal,
    TagsListModal,
    CreateTagsModal,
    NeighborhoodsListModal,
    CreateNeighborhoodsModal,
  },
  created() {
    const vendorId = this.vendorId;
    this.getSingleVendorByID(vendorId);
  },
  props: {
    vendorId: {
      type: String,
      required: false,
    },
    vendorList: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      selectedOption: "",
      isCancelling: false,
      isLoading: false,
      changesMade: false,
      shortDescription: null,
      noDataPlaceholderText: "No information",
      selectedVendor: null,
      addressLine1: "",
      addressLine2: "",
      isSuccessful: false,
      categorySearch: "",
      vendors: [],
      categories: [],
      newCategory: "",
      newGiftCardsFiles: [],
      newVendorLogoFiles: [],
      newBrandingFiles: [],
      existingGiftCardsMedia: [],
      existingVendorLogos: [],
      existingBrandingMedia: [],
      mediaUrl: "",
      showPreview: false,
      debounce: null,
      idOfMediaToDelete: "",
      isConfirmDeleteModalVisible: false,
      vendorIsSaving: false,
      mediaIsSaving: false,
      mediaUploadSuccess: false,
      addressIsSaving: false,
      showProductFailedMessage: false,
      isVendorListModalVisible: false,
      isCategoryListModalVisible: false,
      isNewCategoryModalVisible: false,
      isAddressListModalVisible: false,
      isCreateAddressModalVisible: false,
      addressIsBeingEdited: false,
      isVendorOnlineStorePlatformListModalVisible: false,
      isNewVendorOnlineStorePlatformModalVisible: false,
      isNewTagModalVisible: false,
      isTagsListModalVisible: false,
      isNewNeighborhoodModalVisible: false,
      isNeighborhoodsListModalVisible: false,
      files: [],
      showTooltip: false,
      doesCategoryExist: false,
      doesTagExist: false,
      doesOnlineStorePlatformExist: false,
      doesNeighborhoodExist: false,
      updateFailed: false,
      countryOptions: [
        { text: "Select country", value: "" },
        { text: "United States", value: "US" },
        { text: "Afghanistan", value: "AF" },
        { text: "Åland Islands", value: "AX" },
        { text: "Albania", value: "AL" },
        { text: "Algeria", value: "DZ" },
        { text: "American Samoa", value: "AS" },
        { text: "Andorra", value: "AD" },
        { text: "Angola", value: "AO" },
        { text: "Anguilla", value: "AI" },
        { text: "Antarctica", value: "AQ" },
        { text: "Antigua and Barbuda", value: "AG" },
        { text: "Argentina", value: "AR" },
        { text: "Armenia", value: "AM" },
        { text: "Aruba", value: "AW" },
        { text: "Australia", value: "AU" },
        { text: "Austria", value: "AT" },
        { text: "Azerbaijan", value: "AZ" },
        { text: "Bahamas", value: "BS" },
        { text: "Bahrain", value: "BH" },
        { text: "Bangladesh", value: "BD" },
        { text: "Barbados", value: "BB" },
        { text: "Belarus", value: "BY" },
        { text: "Belgium", value: "BE" },
        { text: "Belize", value: "BZ" },
        { text: "Benin", value: "BJ" },
        { text: "Bermuda", value: "BM" },
        { text: "Bhutan", value: "BT" },
        { text: "Bolivia", value: "BO" },
        { text: "Bosnia and Herzegovina", value: "BA" },
        { text: "Botswana", value: "BW" },
        { text: "Bouvet Island", value: "BV" },
        { text: "Brazil", value: "BR" },
        { text: "British Indian Ocean Territory", value: "IO" },
        { text: "Brunei Darussalam", value: "BN" },
        { text: "Bulgaria", value: "BG" },
        { text: "Burkina Faso", value: "BF" },
        { text: "Burundi", value: "BI" },
        { text: "Cambodia", value: "KH" },
        { text: "Cameroon", value: "CM" },
        { text: "Canada", value: "CA" },
        { text: "Cape Verde", value: "CV" },
        { text: "Cayman Islands", value: "KY" },
        { text: "Central African Republic", value: "CF" },
        { text: "Chad", value: "TD" },
        { text: "Chile", value: "CL" },
        { text: "China", value: "CN" },
        { text: "Christmas Island", value: "CX" },
        { text: "Cocos (Keeling) Islands", value: "CC" },
        { text: "Colombia", value: "CO" },
        { text: "Comoros", value: "KM" },
        { text: "Congo", value: "CG" },
        { text: "Congo, The Democratic Republic of the", value: "CD" },
        { text: "Cook Islands", value: "CK" },
        { text: "Costa Rica", value: "CR" },
        { text: "Cote D'Ivoire", value: "CI" },
        { text: "Croatia", value: "HR" },
        { text: "Cuba", value: "CU" },
        { text: "Cyprus", value: "CY" },
        { text: "Czech Republic", value: "CZ" },
        { text: "Denmark", value: "DK" },
        { text: "Djibouti", value: "DJ" },
        { text: "Dominica", value: "DM" },
        { text: "Dominican Republic", value: "DO" },
        { text: "Ecuador", value: "EC" },
        { text: "Egypt", value: "EG" },
        { text: "El Salvador", value: "SV" },
        { text: "Equatorial Guinea", value: "GQ" },
        { text: "Eritrea", value: "ER" },
        { text: "Estonia", value: "EE" },
        { text: "Ethiopia", value: "ET" },
        { text: "Falkland Islands (Malvinas)", value: "FK" },
        { text: "Faroe Islands", value: "FO" },
        { text: "Fiji", value: "FJ" },
        { text: "Finland", value: "FI" },
        { text: "France", value: "FR" },
        { text: "French Guiana", value: "GF" },
        { text: "French Polynesia", value: "PF" },
        { text: "French Southern Territories", value: "TF" },
        { text: "Gabon", value: "GA" },
        { text: "Gambia", value: "GM" },
        { text: "Georgia", value: "GE" },
        { text: "Germany", value: "DE" },
        { text: "Ghana", value: "GH" },
        { text: "Gibraltar", value: "GI" },
        { text: "Greece", value: "GR" },
        { text: "Greenland", value: "GL" },
        { text: "Grenada", value: "GD" },
        { text: "Guadeloupe", value: "GP" },
        { text: "Guam", value: "GU" },
        { text: "Guatemala", value: "GT" },
        { text: "Guernsey", value: "GG" },
        { text: "Guinea", value: "GN" },
        { text: "Guinea-Bissau", value: "GW" },
        { text: "Guyana", value: "GY" },
        { text: "Haiti", value: "HT" },
        { text: "Heard Island and Mcdonald Islands", value: "HM" },
        { text: "Holy See (Vatican City State)", value: "VA" },
        { text: "Honduras", value: "HN" },
        { text: "Hong Kong", value: "HK" },
        { text: "Hungary", value: "HU" },
        { text: "Iceland", value: "IS" },
        { text: "India", value: "IN" },
        { text: "Indonesia", value: "ID" },
        { text: "Iran, Islamic Republic Of", value: "IR" },
        { text: "Iraq", value: "IQ" },
        { text: "Ireland", value: "IE" },
        { text: "Isle of Man", value: "IM" },
        { text: "Israel", value: "IL" },
        { text: "Italy", value: "IT" },
        { text: "Jamaica", value: "JM" },
        { text: "Japan", value: "JP" },
        { text: "Jersey", value: "JE" },
        { text: "Jordan", value: "JO" },
        { text: "Kazakhstan", value: "KZ" },
        { text: "Kenya", value: "KE" },
        { text: "Kiribati", value: "KI" },
        { text: "Korea, Democratic People'S Republic of", value: "KP" },
        { text: "Korea, Republic of", value: "KR" },
        { text: "Kuwait", value: "KW" },
        { text: "Kyrgyzstan", value: "KG" },
        { text: "Lao People'S Democratic Republic", value: "LA" },
        { text: "Latvia", value: "LV" },
        { text: "Lebanon", value: "LB" },
        { text: "Lesotho", value: "LS" },
        { text: "Liberia", value: "LR" },
        { text: "Libyan Arab Jamahiriya", value: "LY" },
        { text: "Liechtenstein", value: "LI" },
        { text: "Lithuania", value: "LT" },
        { text: "Luxembourg", value: "LU" },
        { text: "Macao", value: "MO" },
        { text: "Macedonia, The Former Yugoslav Republic of", value: "MK" },
        { text: "Madagascar", value: "MG" },
        { text: "Malawi", value: "MW" },
        { text: "Malaysia", value: "MY" },
        { text: "Maldives", value: "MV" },
        { text: "Mali", value: "ML" },
        { text: "Malta", value: "MT" },
        { text: "Marshall Islands", value: "MH" },
        { text: "Martinique", value: "MQ" },
        { text: "Mauritania", value: "MR" },
        { text: "Mauritius", value: "MU" },
        { text: "Mayotte", value: "YT" },
        { text: "Mexico", value: "MX" },
        { text: "Micronesia, Federated States of", value: "FM" },
        { text: "Moldova, Republic of", value: "MD" },
        { text: "Monaco", value: "MC" },
        { text: "Mongolia", value: "MN" },
        { text: "Montserrat", value: "MS" },
        { text: "Morocco", value: "MA" },
        { text: "Mozambique", value: "MZ" },
        { text: "Myanmar", value: "MM" },
        { text: "Namibia", value: "NA" },
        { text: "Nauru", value: "NR" },
        { text: "Nepal", value: "NP" },
        { text: "Netherlands", value: "NL" },
        { text: "Netherlands Antilles", value: "AN" },
        { text: "New Caledonia", value: "NC" },
        { text: "New Zealand", value: "NZ" },
        { text: "Nicaragua", value: "NI" },
        { text: "Niger", value: "NE" },
        { text: "Nigeria", value: "NG" },
        { text: "Niue", value: "NU" },
        { text: "Norfolk Island", value: "NF" },
        { text: "Northern Mariana Islands", value: "MP" },
        { text: "Norway", value: "NO" },
        { text: "Oman", value: "OM" },
        { text: "Pakistan", value: "PK" },
        { text: "Palau", value: "PW" },
        { text: "Palestinian Territory, Occupied", value: "PS" },
        { text: "Panama", value: "PA" },
        { text: "Papua New Guinea", value: "PG" },
        { text: "Paraguay", value: "PY" },
        { text: "Peru", value: "PE" },
        { text: "Philippines", value: "PH" },
        { text: "Pitcairn", value: "PN" },
        { text: "Poland", value: "PL" },
        { text: "Portugal", value: "PT" },
        { text: "Puerto Rico", value: "PR" },
        { text: "Qatar", value: "QA" },
        { text: "Reunion", value: "RE" },
        { text: "Romania", value: "RO" },
        { text: "Russian Federation", value: "RU" },
        { text: "RWANDA", value: "RW" },
        { text: "Saint Helena", value: "SH" },
        { text: "Saint Kitts and Nevis", value: "KN" },
        { text: "Saint Lucia", value: "LC" },
        { text: "Saint Pierre and Miquelon", value: "PM" },
        { text: "Saint Vincent and the Grenadines", value: "VC" },
        { text: "Samoa", value: "WS" },
        { text: "San Marino", value: "SM" },
        { text: "Sao Tome and Principe", value: "ST" },
        { text: "Saudi Arabia", value: "SA" },
        { text: "Senegal", value: "SN" },
        { text: "Serbia and Montenegro", value: "CS" },
        { text: "Seychelles", value: "SC" },
        { text: "Sierra Leone", value: "SL" },
        { text: "Singapore", value: "SG" },
        { text: "Slovakia", value: "SK" },
        { text: "Slovenia", value: "SI" },
        { text: "Solomon Islands", value: "SB" },
        { text: "Somalia", value: "SO" },
        { text: "South Africa", value: "ZA" },
        { text: "South Georgia and the South Sandwich Islands", value: "GS" },
        { text: "Spain", value: "ES" },
        { text: "Sri Lanka", value: "LK" },
        { text: "Sudan", value: "SD" },
        { text: "Suriname", value: "SR" },
        { text: "Svalbard and Jan Mayen", value: "SJ" },
        { text: "Swaziland", value: "SZ" },
        { text: "Sweden", value: "SE" },
        { text: "Switzerland", value: "CH" },
        { text: "Syrian Arab Republic", value: "SY" },
        { text: "Taiwan, Province of China", value: "TW" },
        { text: "Tajikistan", value: "TJ" },
        { text: "Tanzania, United Republic of", value: "TZ" },
        { text: "Thailand", value: "TH" },
        { text: "Timor-Leste", value: "TL" },
        { text: "Togo", value: "TG" },
        { text: "Tokelau", value: "TK" },
        { text: "Tonga", value: "TO" },
        { text: "Trinidad and Tobago", value: "TT" },
        { text: "Tunisia", value: "TN" },
        { text: "Turkey", value: "TR" },
        { text: "Turkmenistan", value: "TM" },
        { text: "Turks and Caicos Islands", value: "TC" },
        { text: "Tuvalu", value: "TV" },
        { text: "Uganda", value: "UG" },
        { text: "Ukraine", value: "UA" },
        { text: "United Arab Emirates", value: "AE" },
        { text: "United Kingdom", value: "GB" },
        { text: "United States Minor Outlying Islands", value: "UM" },
        { text: "Uruguay", value: "UY" },
        { text: "Uzbekistan", value: "UZ" },
        { text: "Vanuatu", value: "VU" },
        { text: "Venezuela", value: "VE" },
        { text: "Viet Nam", value: "VN" },
        { text: "Virgin Islands, British", value: "VG" },
        { text: "Virgin Islands, U.S.", value: "VI" },
        { text: "Wallis and Futuna", value: "WF" },
        { text: "Western Sahara", value: "EH" },
        { text: "Yemen", value: "YE" },
        { text: "Zambia", value: "ZM" },
        { text: "Zimbabwe", value: "ZW" },
      ],
    };
  },
  watch: {
    vendorId: {
      handler(newVal, oldVal) {
        // Do something when 'vendorId' changes
        // console.log("Vendor ID changed:", newVal);
        this.getSingleVendorByID(this.vendorId);

        // Update component data or trigger necessary actions
      },
      immediate: true, // This ensures it triggers on component creation as well
    },
  },
  computed: {
    checkIfFormIsComplete() {
      if (
        !this.getVendorName ||
        !this.vendorCategories ||
        !this.vendorDescription ||
        !this.getTypeformUrl
      ) {
        return false;
      }

      return true;
    },
    getVendorName() {
      return this.selectedVendor.name;
    },
    vendorDescription() {
      return this?.selectedVendor?.description;
    },
    vendorCategories() {
      return this?.selectedVendor?.categories?.length === 0
        ? false
        : this?.selectedVendor?.categories;
    },
    getTypeformUrl() {
      return this.selectedVendor.metadata.typeform_url;
    },
    vendorTags() {
      return this?.selectedVendor?.metadata?.tags?.length === 0
        ? false
        : this?.selectedVendor?.metadata?.tags;
    },
    vendorOnlineStorePlatform() {
      return this?.selectedVendor?.metadata?.onlineStorePlatforms?.length === 0
        ? false
        : this?.selectedVendor?.metadata?.onlineStorePlatforms;
    },
    vendorNeighborhoods() {
      return this?.selectedVendor?.metadata?.neighborhoods?.length === 0
        ? false
        : this?.selectedVendor?.metadata?.neighborhoods;
    },
    vendorAddressId() {
      return this?.selectedVendor?.address?._id;
    },
    vendorContactName() {
      return this?.selectedVendor?.contact?.name;
    },
    getNewGiftCardsMedia() {
      if (this.newGiftCardsFiles.length === 0) {
        return false;
      }
      return this.newGiftCardsFiles;
    },
    getNewVendorLogosMedia() {
      if (this.newVendorLogoFiles.length === 0) {
        return false;
      }
      return this.newVendorLogoFiles;
    },
    getNewBrandingMedia() {
      if (this.newBrandingFiles.length === 0) {
        return false;
      }
      return this.newBrandingFiles;
    },
    getExistingGiftCardsMedia() {
      if (this.existingGiftCardsMedia.length === 0) {
        return false;
      }
      return this.existingGiftCardsMedia;
    },
    getExistingVendorMediaLogos() {
      if (this.existingVendorLogos.length === 0) {
        return false;
      }
      return this.existingVendorLogos;
    },
    getExistingBrandingMedia() {
      if (this.existingBrandingMedia.length === 0) {
        return false;
      }
      return this.existingBrandingMedia;
    },
    getVendorSearchQuery() {
      return this.vendorSearchQuery;
    },
    nextVendorId() {
      const currentIndex = this.vendorList.findIndex(
        (vendor) => vendor.id === this.vendorId
      );

      if (currentIndex < this.vendorList.length - 1) {
        const nextVendorId = this.vendorList[currentIndex + 1].id;
        // console.log("Next vendor ID:", nextVendorId);
        // You can use nextVendorId for further operations or return it if needed
        return nextVendorId;
      } else {
        // console.log("Already on the last vendor");
        // Return null or handle the scenario where it's already the last vendor
        return null;
      }
    },
    previousVendorId() {
      const currentIndex = this.vendorList.findIndex(
        (vendor) => vendor.id === this.vendorId
      );

      if (currentIndex > 0) {
        const previousVendorId = this.vendorList[currentIndex - 1].id;
        // console.log("Previous vendor ID:", previousVendorId);
        // You can use previousVendorId for further operations or return it if needed
        return previousVendorId;
      } else {
        // console.log("Already on the first vendor");
        // Return null or handle the scenario where it's already the first vendor
        return null;
      }
    },
    isGiftCardMinimumValid() {
      const minValue = 1;
      return (
        parseFloat(this.selectedVendor.metadata.gift_card_minimum) >= minValue
      );
    },
    isGiftCardValueTypeValid() {
      return typeof this.selectedVendor.metadata.gift_card_minimum == "number";
    },
  },
  methods: {
    async getSingleVendorByID(vendorId) {
      // console.log("Route Updated vendorId:", vendorId);
      const api = await this.getBoldClient();
      this.isLoading = true;

      const {
        data: {
          result: { records: singleVendorById },
        },
      } = await api.listVendors(
        {
          limit: 1,
          expand: "media contact address",
        },
        { filter: { _id: vendorId } }
      );

      this.handleVendorData(singleVendorById[0], api);
    },
    async reloadVendorData() {
      const api = await this.getBoldClient();
      const {
        data: {
          result: { records: singleVendorById },
        },
      } = await api.listVendors(
        {
          limit: 1,
          expand: "media contact address",
        },
        { filter: { _id: this.vendorId } }
      );

      this.handleVendorData(singleVendorById[0], api);
    },
    async updateThisVendor() {
      this.vendorIsSaving = true;
      try {
        const api = await this.getBoldClient();
        const patchObject = this.getPatchObject();

        const {
          data: { result },
        } = await api.upsertVendor(null, {
          filter: { _id: this.vendorId },
          patch: patchObject,
        });

        // Check if newVendorLogoFiles is not an empty array and make the API call
        if (this.newVendorLogoFiles && this.newVendorLogoFiles.length > 0) {
          await this.submitLogosMedia();
        }

        // Check if newGiftCardsFiles is not an empty array and make the API call
        if (this.newGiftCardsFiles && this.newGiftCardsFiles.length > 0) {
          await this.submitGiftCardsMedia();
        }

        // Check if newBrandingFiles is not an empty array and make the API call
        if (this.newBrandingFiles && this.newBrandingFiles.length > 0) {
          await this.submitBrandingMedia();
        }

        this.handleVendorData(result, api);
        this.handleSuccess("vendorUpdate"); // Pass 'vendorUpdate' as a type of success
      } catch (error) {
        this.handleError(error);
      }
    },
    async submitLogosMedia() {
      await this.uploadMedia("logos", this.newVendorLogoFiles);
    },
    async submitGiftCardsMedia() {
      await this.uploadMedia("gift cards", this.newGiftCardsFiles);
    },
    async submitBrandingMedia() {
      await this.uploadMedia("vendor branding", this.newBrandingFiles);
    },
    async uploadMedia(tag, files) {
      try {
        const api = await this.getBoldClient();
        this.mediaIsSaving = true;

        const axiosOpts = {
          headers: { "Content-Type": "multipart/form-data" },
        };

        const uploadPromises = files.map(async (file) => {
          let formData = new FormData();
          formData.append("tags", tag);
          formData.append("vendor", this.vendorId);
          formData.append("media", file.file);
          return await api.uploadMedia(null, formData, axiosOpts);
        });

        await Promise.all(uploadPromises);

        this.handleSuccess("mediaUpdate");
      } catch (error) {
        this.handleError(error);
      }
    },
    async deleteThisMediaFile() {
      const api = await this.getBoldClient();
      try {
        const response = await api.deleteMedia(null, {
          filter: { _id: this.idOfMediaToDelete },
        });
        this.handleSuccess("deleteUpdate");
        this.reloadVendorData();
      } catch (error) {
        this.handleError(error);
      }
    },
    async updateAddress() {
      this.addressIsSaving = true;
      this.addressIsBeingEdited = false;

      try {
        // Get the Bold client
        const api = await this.getBoldClient();

        // Prepare address lines
        const addressLines = [];
        if (this?.selectedVendor?.address?.lines[0]) {
          addressLines.push(this.selectedVendor.address.lines[0]);
        }
        if (this?.selectedVendor?.address?.lines[1]) {
          addressLines.push(this.selectedVendor.address.lines[1]);
        }

        // Create an address object
        let newAddress = {
          city: this.selectedVendor.address.city,
          state: this.selectedVendor.address.state,
          postalCode: this.selectedVendor.address.postalCode,
          country: this.selectedVendor.address.country,
        };

        // Conditionally add the 'lines' property
        if (addressLines.length > 0) {
          newAddress.lines = addressLines;
        }

        // Create the address using the API
        const response = await api.upsertAddress(null, {
          filter: {
            _id: this.selectedVendor.address._id,
          },
          patch: newAddress,
        });

        // Handle a successful response
        // console.log("address updated", response);
        this.addressIsSaving = false;

        const address = response.data.result.records[0];
        this.setNewAddress(address);
      } catch (error) {
        console.error("Error updating address", error);

        // Handle the error as needed
        console.log("address was NOT created");
      }
    },
    getPatchObject() {
      const patchObject = {
        name: this.selectedVendor.name,
        categories: this.selectedVendor.categories,
        description: this.selectedVendor.description,
        contact: this.selectedVendor.contact,
        "metadata.shortDescription":
          this.selectedVendor.metadata.shortDescription,
        "metadata.website_url": this.selectedVendor.metadata.website_url,
        "metadata.twitter_url": this.selectedVendor.metadata.twitter_url,
        "metadata.linkedin_url": this.selectedVendor.metadata.linkedin_url,
        "metadata.tiktok_url": this.selectedVendor.metadata.tiktok_url,
        "metadata.instagram_url": this.selectedVendor.metadata.instagram_url,
        "metadata.onlineStorePlatforms":
          this.selectedVendor.metadata.onlineStorePlatforms,
        "metadata.gift_card_page": this.selectedVendor.metadata.gift_card_page,
        "metadata.tags": this.selectedVendor.metadata.tags,
        "metadata.neighborhoods": this.selectedVendor.metadata.neighborhoods,
        "metadata.gift_card_minimum":
          this.selectedVendor.metadata.gift_card_minimum,
        "metadata.typeform_url": this.selectedVendor.metadata.typeform_url,
      };

      // console.log(this?.selectedVendor?.address?._id || "no address");
      if (
        this?.selectedVendor?.address?._id ||
        this?.selectedVendor?.address === null
      ) {
        if (this?.selectedVendor?.address?._id) {
          patchObject.address = this.selectedVendor.address._id;
        } else {
          patchObject.address = null; // If address is null
        }
      }

      return patchObject;
    },
    handleVendorData(vendorData, api) {
      // console.log("vendor", vendorData);
      this.selectedVendor = this.extractVendorDetails(vendorData);
      const mediaArr = vendorData?.media || [];

      const logos = this.filterMediaByTag(mediaArr, "logos");
      const giftCardsMedia = this.filterMediaByTag(mediaArr, "gift cards");
      const brandingMedia = this.filterMediaByTag(mediaArr, "vendor branding");

      this.createMediaUrls(logos, this.existingVendorLogos, api);
      this.createMediaUrls(giftCardsMedia, this.existingGiftCardsMedia, api);
      this.createMediaUrls(brandingMedia, this.existingBrandingMedia, api);

      this.setupDeepWatchers(this.selectedVendor);
      this.changesMade = false;
      this.isLoading = false;
    },
    extractVendorDetails(vendorData) {
      const {
        name = "",
        description = "",
        categories = [],
        contact: {
          name: contactName = "",
          title = "",
          email = "",
          phone = "",
        } = {},
        address,
        metadata: {
          shortDescription = "",
          website_url = "",
          twitter_url = "",
          linkedin_url = "",
          tiktok_url = "",
          instagram_url = "",
          gift_card_page = "",
          gift_card_minimum = "",
          typeform_url = "",

          tags = [],
          onlineStorePlatforms = [],
          neighborhoods = [],
        } = {},
      } = vendorData;

      if (!address || !Object.keys(address).length) {
        return {
          name,
          description,
          categories,
          contact: { name: contactName, title, email, phone },
          metadata: {
            shortDescription,
            website_url,
            twitter_url,
            linkedin_url,
            tiktok_url,
            instagram_url,
            onlineStorePlatforms,
            gift_card_page,
            gift_card_minimum,
            tags,
            neighborhoods,
            typeform_url,
          },
        };
      }

      // Check if 'lines' exists within 'address' and is an array
      const { lines: addressLines = [] } = address;
      const [line1 = "", line2 = ""] = addressLines;

      const {
        city = "",
        state = "",
        postalCode = "",
        country = "",
        _id: addressId = "",
      } = address;

      return {
        name,
        description,
        categories,
        contact: { name: contactName, title, email, phone },
        address: {
          lines: [line1, line2],
          city,
          state,
          postalCode,
          country,
          _id: addressId,
        },
        metadata: {
          shortDescription,
          website_url,
          twitter_url,
          linkedin_url,
          tiktok_url,
          instagram_url,
          gift_card_page,
          gift_card_minimum,
          tags,
          onlineStorePlatforms,
          neighborhoods,
          typeform_url,
        },
      };
    },
    setupArrayWatcher(arr) {
      arr.forEach((item, index) => {
        this.$watch(
          () => arr[index],
          (newValue, oldValue) => {
            if (newValue !== oldValue) {
              this.changesMade = true;
              // console.log(
              //   `Change detected at index ${index}:`,
              //   oldValue,
              //   "->",
              //   newValue
              // );
            }
          }
        );
      });
    },
    setupDeepWatchers(obj) {
      for (const key in obj) {
        if (Array.isArray(obj[key]) && key === "onlineStorePlatforms") {
          // If it's the 'onlineStorePlatforms' array, set up the array watcher
          this.setupArrayWatcher(obj[key]);
        } else if (Array.isArray(obj[key])) {
          // If it's an array (excluding 'onlineStorePlatforms'), set up a shallow watcher
          // This will watch the array reference itself, not the elements inside it
          this.$watch(
            () => obj[key],
            () => {
              this.changesMade = true;
              // console.log(`Change detected in ${key}`);
            }
          );
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          // If the property is an object, set up a deep watcher recursively
          this.setupDeepWatchers(obj[key]);
        } else {
          // Set up a watcher for non-array properties
          this.$watch(
            () => obj[key],
            (newValue, oldValue) => {
              if (newValue !== oldValue) {
                this.changesMade = true;
                // console.log(
                //   `Change detected in ${key}:`,
                //   oldValue,
                //   "->",
                //   newValue
                // );
              }
            }
          );
        }
      }
    },
    /** media */
    filterMediaByTag(mediaArr, tag) {
      return mediaArr.filter((media) => {
        return media.metadata.tags.includes(tag);
      });
    },
    createMediaUrls(mediaArr, mediaUrlArr, api) {
      if (mediaArr.length === 0) {
        this.mediaUrl = "";
      }

      mediaUrlArr.length = 0;

      for (const media of mediaArr) {
        const mediaId = media._id;
        const mediaUrl = api.utils.getMediaUrl(mediaId);
        mediaUrlArr.push({ _id: mediaId, url: mediaUrl });
      }
    },
    handleLogoMediaUploads(event) {
      // console.log("handleLogoMediaUploads");

      let uploadedFiles = event.target.files;

      for (var i = 0; i < uploadedFiles.length; i++) {
        const uniqueId = Math.random().toString(36).substr(2, 9); // Generate a unique ID
        const fileWithId = {
          id: uniqueId,
          file: uploadedFiles[i],
        };
        this.newVendorLogoFiles.push(fileWithId);
      }
      this.getVendorLogosImagePreviews();
    },
    handleGiftCardsMediaUploads(event) {
      // console.log("handleGiftCardsMediaUploads");
      let uploadedFiles = event.target.files;

      for (var i = 0; i < uploadedFiles.length; i++) {
        const uniqueId = Math.random().toString(36).substr(2, 9); // Generate a unique ID
        const fileWithId = {
          id: uniqueId,
          file: uploadedFiles[i],
        };
        this.newGiftCardsFiles.push(fileWithId);
      }
      this.getGiftCardsImagePreviews();
    },
    handleBrandingMediaUploads(event) {
      // console.log("handleBrandingMediaUploads");
      let uploadedFiles = event.target.files;

      for (var i = 0; i < uploadedFiles.length; i++) {
        const uniqueId = Math.random().toString(36).substr(2, 9); // Generate a unique ID
        const fileWithId = {
          id: uniqueId,
          file: uploadedFiles[i],
        };
        this.newBrandingFiles.push(fileWithId);
      }
      this.getBrandingImagePreviews();
    },
    getVendorLogosImagePreviews() {
      this.newVendorLogoFiles.forEach((file) => {
        if (/\.(jpe?g|png|gif)$/i.test(file.file.name)) {
          let reader = new FileReader();

          reader.onload = () => {
            let imgElement = document.getElementById("image-" + file.id);
            if (imgElement) {
              imgElement.src = reader.result;
            }
          };

          reader.readAsDataURL(file.file);
        }
      });
    },
    getGiftCardsImagePreviews() {
      this.newGiftCardsFiles.forEach((file) => {
        if (/\.(jpe?g|png|gif)$/i.test(file.file.name)) {
          let reader = new FileReader();

          reader.onload = () => {
            let imgElement = document.getElementById("image-" + file.id);
            if (imgElement) {
              imgElement.src = reader.result;
            }
          };

          reader.readAsDataURL(file.file);
        }
      });
    },
    getBrandingImagePreviews() {
      this.newBrandingFiles.forEach((file) => {
        if (/\.(jpe?g|png|gif)$/i.test(file.file.name)) {
          let reader = new FileReader();

          reader.onload = () => {
            let imgElement = document.getElementById("image-" + file.id);
            if (imgElement) {
              imgElement.src = reader.result;
            }
          };

          reader.readAsDataURL(file.file);
        }
      });
    },
    removeUploadedGiftCardsMediaFile(key) {
      this.newGiftCardsFiles.splice(key, 1);
    },
    removeUploadedLogoMediaFile(key) {
      this.newVendorLogoFiles.splice(key, 1);
    },
    removeUploadedBrandingMediaFile(key) {
      this.newBrandingFiles.splice(key, 1);
    },
    returnToVendorList() {
      this.$router.push({ path: "/vendors" });
    },
    //** categories */
    showCreateCategoryModal() {
      this.isNewCategoryModalVisible = true;
    },
    closeCreateCategoryModal() {
      this.isNewCategoryModalVisible = false;
    },
    showCategoryListModal() {
      this.isCategoryListModalVisible = true;
    },
    handleNewCategories(newCategories) {
      // Handle the array of selected categories
      for (const category of newCategories) {
        this.setExistingCategory(category);
      }
      this.isCategoryListModalVisible = false;
    },
    setExistingCategory(category) {
      const existingCategory = this.selectedVendor.categories.find(
        (cat) => cat === category
      );

      if (!existingCategory) {
        this.selectedVendor.categories.push(category);
        this.doesCategoryExist = false;
      } else {
        this.doesCategoryExist = true;
        setTimeout(() => {
          this.doesCategoryExist = false;
        }, 2000);
      }
      this.isNewCategoryModalVisible = false;
    },
    removeCategorySelected(category) {
      let array = this.selectedVendor.categories;
      let index = array.indexOf(category);
      if (index > -1) {
        array.splice(index, 1);
      }
      return array;
    },
    //** tags */
    showCreateTagsModal() {
      this.isNewTagModalVisible = !this.isNewTagModalVisible;
    },
    showTagsListModal() {
      this.isTagsListModalVisible = !this.isTagsListModalVisible;
    },
    handleNewTags(newTags) {
      // Handle the array of selected categories
      for (const tag of newTags) {
        this.setExistingTag(tag);
      }
      this.isNewTagModalVisible = false;
    },
    setExistingTag(tag) {
      const existingStore = this.selectedVendor.metadata.tags.find(
        (s) => s === tag
      );

      if (!existingStore) {
        this.selectedVendor.metadata.tags.push(tag);
        this.doesTagExist = false;
      } else {
        this.doesTagExist = true;
        setTimeout(() => {
          this.doesTagExist = false;
        }, 2000);
      }
      this.isNewTagModalVisible = false;
      this.isTagsListModalVisible = false;
    },
    removeTagsSelected(tag) {
      let array = this.selectedVendor.metadata.tags;
      let index = array.indexOf(tag);
      if (index > -1) {
        array.splice(index, 1);
      }
      return array;
    },
    closeCategoryListModal() {
      this.isCategoryListModalVisible = false;
    },
    closeTagsListModal() {
      this.isTagsListModalVisible = false;
    },
    closeCreateTagModal() {
      this.isNewTagModalVisible = false;
    },
    //** online store platforms */
    showCreateOnlineStorePlatformModal() {
      this.isNewVendorOnlineStorePlatformModalVisible =
        !this.isNewVendorOnlineStorePlatformModalVisible;
    },
    showVendorOnlineStoreListModal() {
      this.isVendorOnlineStorePlatformListModalVisible =
        !this.isVendorOnlineStorePlatformListModalVisible;
    },
    closeOnlineStorePlatformsListModal() {
      this.isVendorOnlineStorePlatformListModalVisible = false;
    },
    closeCreateOnlineStorePlatformModal() {
      this.isNewVendorOnlineStorePlatformModalVisible = false;
    },
    handleNewOnlineStorePlatform(newStores) {
      // Handle the array of selected categories
      for (const store of newStores) {
        this.setExistingOnlineStorePlatform(store);
      }
      this.isVendorOnlineStorePlatformListModalVisible = false;
    },
    setExistingOnlineStorePlatform(store) {
      const existingStore =
        this.selectedVendor.metadata.onlineStorePlatforms.find(
          (s) => s === store
        );

      if (!existingStore) {
        this.selectedVendor.metadata.onlineStorePlatforms.push(store);
        this.doesOnlineStorePlatformExist = false;
      } else {
        this.doesOnlineStorePlatformExist = true;
        setTimeout(() => {
          this.doesOnlineStorePlatformExist = false;
        }, 2000);
      }

      this.isVendorOnlineStorePlatformListModalVisible = false;
      this.isNewVendorOnlineStorePlatformModalVisible = false;
    },
    removeOnlineStorePlatformSelected(store) {
      let array = this.selectedVendor.metadata.onlineStorePlatforms;
      let index = array.indexOf(store);
      if (index > -1) {
        array.splice(index, 1);
      }
      return array;
    },
    /** neighborhoods actions */
    showCreateNeighborhoodModal() {
      this.isNewNeighborhoodModalVisible = !this.isNewNeighborhoodModalVisible;
    },
    closeCreateNeighborhoodModal() {
      this.isNewNeighborhoodModalVisible = false;
    },
    showNeighborhoodsListModal() {
      this.isNeighborhoodsListModalVisible =
        !this.isNeighborhoodsListModalVisible;
    },
    closeNeighborhoodsListModal() {
      this.isNeighborhoodsListModalVisible = false;
    },
    handleNewNeighborhoods(newNeighborhoods) {
      // Handle the array of selected categories
      for (const neighborhood of newNeighborhoods) {
        this.setExistingNeighborhood(neighborhood);
      }
      this.isNeighborhoodsListModalVisible = false;
    },
    setExistingNeighborhood(neighborhood) {
      const existingNeighborhood =
        this.selectedVendor.metadata.neighborhoods.find(
          (n) => n === neighborhood
        );

      if (!existingNeighborhood) {
        this.selectedVendor.metadata.neighborhoods.push(neighborhood);
        this.doesNeighborhoodExist = false;
      } else {
        this.doesNeighborhoodExist = true;
        setTimeout(() => {
          this.doesNeighborhoodExist = false;
        }, 2000);
      }

      this.isNeighborhoodsListModalVisible = false;
      this.isNewNeighborhoodModalVisible = false;
    },
    removeNeighborhoodSelected(neighborhood) {
      let array = this.selectedVendor.metadata.neighborhoods;
      let index = array.indexOf(neighborhood);
      if (index > -1) {
        array.splice(index, 1);
      }
      return array;
    },
    //** addresses */
    async resetAddress() {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();

      api
        .listAddresses(
          {
            limit: 1,
          },
          { filter: { _id: this.selectedVendor.address._id } }
        )
        .then((response) => {
          // console.log("address reset query", response.data.result.records[0]);
          this.selectedVendor.address = response.data.result.records[0];
        })
        .catch((error) => {
          console.log("address reset query failed", error);
        });
    },
    async editAddress() {
      await this.resetAddress();
      this.addressIsBeingEdited = !this.addressIsBeingEdited;
    },
    showCreateAddressModal() {
      this.isCreateAddressModalVisible = !this.isCreateAddressModalVisible;
    },
    showAddressListModal() {
      this.isAddressListModalVisible = !this.isAddressListModalVisible;
    },
    setNewAddress(address) {
      this.selectedVendor.address = address;
      this.isCreateAddressModalVisible = false;
      this.isAddressListModalVisible = false;
    },
    //** navigation */
    cancelCreate() {
      this.$router.push(`/vendors`);
    },
    closeSuccessModal() {
      window.location.reload();
      this.isSuccessful = false;
    },
    showConfirmDeleteModal(idOfMedia) {
      this.idOfMediaToDelete = idOfMedia;
      this.isConfirmDeleteModalVisible = true;
    },
    closeConfirmDeleteModal() {
      this.isConfirmDeleteModalVisible = false;
    },
    /** error handling */
    handleSuccess(type) {
      if (type === "mediaUpdate") {
        console.log("Media upload successful!");
        this.newVendorLogoFiles = [];
        this.newGiftCardsFiles = [];
        this.newBrandingFiles = [];
      } else if (type === "vendorUpdate") {
        console.log("Vendor update successful!");
      } else {
        this.isConfirmDeleteModalVisible = false;
        console.log("generic update successful!");
      }
      this.isSuccessful = true;
      this.vendorIsSaving = false;
    },
    handleError(error) {
      console.error("Error updating vendor: ", error);
      this.vendorIsSaving = false;
      this.updateFailed = true;
      setTimeout(() => {
        this.updateFailed = false;
      }, 5000);
    },
  },
};
</script>

<style scoped>
ul {
  max-height: 200px;
  overflow: auto;
  cursor: pointer;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.custom-tooltip {
  position: absolute;
  top: -35px; /* Adjust the positioning as needed */
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: #fff;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
  z-index: 999;
  display: inline-block;
  opacity: 0.9;
  transition: opacity 0.3s ease-in-out;
}

.custom-tooltip::before {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.card-img {
  object-fit: contain;
}

.dropdown-menu {
  max-height: 280px;
  overflow-y: auto;
}
</style>
